/**
 * Shared date+time formatting for a screen timestamp ("Updated 28 Sep 2026, 16:47", an offer
 * history entry, …) — owner correction 2026-09-28 (PR3 review): the offer pages' own
 * `new Date(iso).toLocaleString(getLocale())` produced `9/28/2026, 4:47:15 PM` — numeric-month US
 * ordering (ambiguous outside the US) plus seconds nobody needed.
 *
 * `dateStyle: "medium"` gives a NAMED month (unambiguous, same reasoning `editor/print-rider.ts`'s
 * `printDate()` already used for the printed sheet — except THIS stays locale-aware, since a screen
 * reads in whatever language the viewer picked, unlike a printed document that travels to a stranger
 * and is deliberately English). `timeStyle: "short"` gives hours:minutes, no seconds.
 */
import { getLocale } from "./i18n";

/**
 * A time of day, on the 24-hour clock in every locale: «14:14», never «02:14 PM». Set times in the product are written
 * «21:00» (`set_start`), and a departure («Departed 14:14») reads beside them (SCRUM-760, owner's round). `hourCycle: "h23"`
 * rather than `hour12: false`: the latter can print «24:05» for just after midnight in some engines. The zone is the
 * viewer's own unless one is given (tests).
 */
export function clockTime(iso: string, locale: string = getLocale(), timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hourCycle: "h23", ...(timeZone ? { timeZone } : {}) });
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(getLocale(), { dateStyle: "medium", timeStyle: "short", hourCycle: "h23" });
}

/**
 * The full stamp a list shows («10/30/2026, 16:05:09»): the locale's own day order, as before, but the 24-hour clock in every
 * locale (owner, 30.10.2026: «время стало am/pm в кабинете райдеров… у нас 24 часовая система»). `locale` and `timeZone` are
 * for tests; a screen passes neither.
 */
export function formatStamp(iso: string, locale: string = getLocale(), timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(locale, { hourCycle: "h23", ...(timeZone ? { timeZone } : {}) });
}

/**
 * A day for the ONE small line under a title on a phone («Oct 19», «Oct 19, 2025» in another year): the same named
 * month `formatDateTime` uses, no time. `now` is injectable for tests.
 */
export function formatDayShort(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(getLocale(), { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}
